import { Inject, Injectable } from '@angular/core';
import {
  ChartDataRequest,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwQuery,
  DwQueryFilterGroup,
  DwQueryHelper
} from '@devwareapps/devware-cap';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {
  ReportDashboardConfigRecord,
  ReportDashboardGroupConfigRecord,
  ReportDashboardWidgetConfigRecord
} from '../models/report-dashboard-config-record.model';
import { ReportDashboardDefinition, ReportWidgetDefinition } from '../models/report-dashboard-definition.model';
import { ReportDashboardGroupDefinition } from '../models/report-dashboard-group-definition.model';
import { ActiveFilter } from '../../../shared/models/active-filter.model';

const CONFIG_ITEMS = {
  dashboardGroup: 'ReportDashboardGroup',
  dashboard: 'ReportDashboard',
  widget: 'ReportDashboardWidget'
};

const CURRENT_SCHEMA_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class ReportDashboardConfigService {
  constructor(@Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService) { }

  loadDashboardGroup(groupKey: string): Observable<ReportDashboardGroupDefinition> {
    return this.loadSingleRecord<ReportDashboardGroupConfigRecord>(
      CONFIG_ITEMS.dashboardGroup, 'DashboardGroupKey', groupKey
    ).pipe(map(record => this.parseDashboardGroupRecord(record)));
  }

  loadDashboardGroupRecord(groupKey: string): Observable<ReportDashboardGroupConfigRecord> {
    return this.loadSingleRecord<ReportDashboardGroupConfigRecord>(
      CONFIG_ITEMS.dashboardGroup, 'DashboardGroupKey', groupKey
    );
  }

  saveDashboardGroupRecord(record: ReportDashboardGroupConfigRecord): Observable<ReportDashboardGroupConfigRecord> {
    return this.ormDataService.saveEntity(record).pipe(
      map(saved => saved as unknown as ReportDashboardGroupConfigRecord)
    );
  }

  loadDashboard(dashboardKey: string): Observable<ReportDashboardDefinition> {
    return this.loadSingleRecord<ReportDashboardConfigRecord>(
      CONFIG_ITEMS.dashboard, 'DashboardKey', dashboardKey
    ).pipe(mergeMap(record => this.parseDashboardRecord(record)));
  }

  loadDashboardRecord(dashboardKey: string): Observable<ReportDashboardConfigRecord> {
    return this.loadSingleRecord<ReportDashboardConfigRecord>(
      CONFIG_ITEMS.dashboard, 'DashboardKey', dashboardKey
    );
  }

  saveDashboardRecord(record: ReportDashboardConfigRecord): Observable<ReportDashboardConfigRecord> {
    return this.ormDataService.saveEntity(record).pipe(
      map(saved => saved as unknown as ReportDashboardConfigRecord)
    );
  }

  saveDashboardDefaultFilters(
    dashboardKey: string,
    activeFilters: ActiveFilter[]
  ): Observable<ReportDashboardDefinition> {
    return this.loadDashboardRecord(dashboardKey).pipe(mergeMap(record => {
      const storedDefinition = this.deepMerge(
        undefined,
        this.parseJson<any>(record.DashboardConfigJSON, `dashboard ${dashboardKey}`)
      );
      delete storedDefinition.contextNames;
      const activeByField = (activeFilters || []).reduce((result, filter) => {
        result[filter.field] = filter;
        return result;
      }, {} as { [field: string]: ActiveFilter });

      storedDefinition.filters = (storedDefinition.filters || []).map(filter => {
        const storedFilter = this.deepMerge(undefined, filter);
        const config = storedFilter.config || {};
        const activeFilter = activeByField[config.attr];

        if (activeFilter) {
          config.defaultValue = this.cloneJsonValue(activeFilter.value);
        } else {
          delete config.defaultValue;
        }

        // Saving the default view captures its current values explicitly. A
        // configured "first lookup option" must not restore a cleared filter
        // or replace the value selected by the administrator.
        if (config.additionalConfig) {
          delete config.additionalConfig.defaultToFirstLookupOption;
        }

        storedFilter.config = config;
        return storedFilter;
      });

      record.DashboardConfigJSON = JSON.stringify(storedDefinition, null, 2);
      return this.parseDashboardRecord(record).pipe(
        mergeMap(() => this.saveDashboardRecord(record)),
        mergeMap(() => this.loadDashboard(dashboardKey))
      );
    }));
  }

  createDashboardRecord(values: Partial<ReportDashboardConfigRecord>): Observable<ReportDashboardConfigRecord> {
    const record = this.ormDataService.createDummyEntity(CONFIG_ITEMS.dashboard, values);
    return this.ormDataService.saveEntity(record).pipe(
      map(saved => saved as unknown as ReportDashboardConfigRecord)
    );
  }

  loadDashboards(dashboardKeys: string[]): Observable<{ [key: string]: ReportDashboardDefinition }> {
    const keys = this.uniqueStrings(dashboardKeys);
    if (!keys.length) {
      return of({});
    }

    return forkJoin(keys.map(key => this.loadDashboard(key))).pipe(map(definitions =>
      definitions.reduce((result, definition) => {
        result[definition.key] = definition;
        return result;
      }, {} as { [key: string]: ReportDashboardDefinition })
    ));
  }

  loadDashboardGroupRecords(): Observable<ReportDashboardGroupConfigRecord[]> {
    return this.loadRecords<ReportDashboardGroupConfigRecord>(CONFIG_ITEMS.dashboardGroup);
  }

  loadDashboardRecords(): Observable<ReportDashboardConfigRecord[]> {
    return this.loadRecords<ReportDashboardConfigRecord>(CONFIG_ITEMS.dashboard);
  }

  loadReusableWidgetRecords(): Observable<ReportDashboardWidgetConfigRecord[]> {
    return this.loadRecords<ReportDashboardWidgetConfigRecord>(CONFIG_ITEMS.widget);
  }

  parseDashboardGroupRecord(record: ReportDashboardGroupConfigRecord): ReportDashboardGroupDefinition {
    const config = this.parseJson<any>(record?.DashboardGroupConfigJSON, `dashboard group ${record?.DashboardGroupKey}`);
    const definition: ReportDashboardGroupDefinition = {
      ...config,
      // Groups saved before global contexts moved out of dashboards relied on
      // the page's implicit pilexosUser context.
      contextNames: config.contextNames === undefined ? ['pilexosUser'] : config.contextNames,
      key: record.DashboardGroupKey
    };
    const errors = this.validateDashboardGroup(definition);
    if (errors.length) {
      throw new Error(`Dashboard group ${record.DashboardGroupKey} is invalid: ${errors.join(' ')}`);
    }
    return definition;
  }

  parseDashboardRecord(record: ReportDashboardConfigRecord): Observable<ReportDashboardDefinition> {
    const config = this.removeFilterVisibility(
      this.parseJson<any>(record?.DashboardConfigJSON, `dashboard ${record?.DashboardKey}`)
    );
    const definition: ReportDashboardDefinition = {
      ...config,
      key: record.DashboardKey,
      widgets: config.widgets || []
    };

    return this.resolveReusableWidgets(definition).pipe(map(resolved => {
      const normalized = this.normalizeDashboard(resolved);
      const errors = this.validateDashboard(normalized);
      if (errors.length) {
        throw new Error(`Dashboard ${record.DashboardKey} is invalid: ${errors.join(' ')}`);
      }
      return normalized;
    }));
  }

  parseReusableWidgetRecord(record: ReportDashboardWidgetConfigRecord): ReportWidgetDefinition {
    const config = this.parseJson<any>(record?.WidgetConfigJSON, `reusable widget ${record?.WidgetKey}`);
    if (config.schemaVersion !== CURRENT_SCHEMA_VERSION) {
      throw new Error(`Reusable widget ${record.WidgetKey} schemaVersion must be ${CURRENT_SCHEMA_VERSION}.`);
    }
    const definition = this.normalizeWidget({
      ...config,
      key: config.key || record.WidgetKey
    });
    const errors = this.validateWidget(definition);
    if (errors.length) {
      throw new Error(`Reusable widget ${record.WidgetKey} is invalid: ${errors.join(' ')}`);
    }
    return definition;
  }

  validateDashboardGroup(definition: ReportDashboardGroupDefinition): string[] {
    const errors: string[] = [];
    if (definition?.schemaVersion !== CURRENT_SCHEMA_VERSION) {
      errors.push(`schemaVersion must be ${CURRENT_SCHEMA_VERSION}.`);
    }
    if (!definition?.title) {
      errors.push('A title is required.');
    }
    if (!Array.isArray(definition?.contextNames)) {
      errors.push('contextNames must be an array.');
    }
    if (!Array.isArray(definition?.tabs) || !definition.tabs.length) {
      errors.push('At least one dashboard tab is required.');
      return errors;
    }

    definition.tabs.forEach((tab, index) => {
      if (!tab?.dashboardKey || !tab?.display) {
        errors.push(`Tab ${index + 1} requires dashboardKey and display.`);
      }
    });
    this.addDuplicateErrors(definition.tabs.map(tab => tab.dashboardKey), 'dashboard tab key', errors);
    if (definition.defaultDashboardKey
      && !definition.tabs.some(tab => tab.dashboardKey === definition.defaultDashboardKey)) {
      errors.push('defaultDashboardKey must reference a configured tab.');
    }
    return errors;
  }

  validateDashboard(definition: ReportDashboardDefinition): string[] {
    const errors: string[] = [];
    if (definition?.schemaVersion !== CURRENT_SCHEMA_VERSION) {
      errors.push(`schemaVersion must be ${CURRENT_SCHEMA_VERSION}.`);
    }
    if (!definition?.title) {
      errors.push('A title is required.');
    }
    if (!Array.isArray(definition?.widgets)) {
      errors.push('widgets must be an array.');
      return errors;
    }
    if (definition.layoutColumnCount && definition.layoutColumnCount < 1) {
      errors.push('layoutColumnCount must be greater than zero.');
    }

    (definition.filters || []).forEach((filter, index) => {
      if (!filter?.config?.attr || !filter?.config?.filterType
        || !filter?.config?.additionalConfig?.presentationType) {
        errors.push(`Filter ${index + 1} requires an attribute, filter type, and presentation type.`);
      }
    });
    (definition.globalFilters || []).forEach((filter, index) => {
      if (!filter?.dataSourceItemName || !filter?.filterExpression) {
        errors.push(`Global filter ${index + 1} requires a data source and filter expression.`);
      }
    });
    (definition.summaries || []).forEach((summary, index) => {
      if (!summary?.key || !summary?.measures?.length) {
        errors.push(`Summary ${index + 1} requires a key and at least one measure.`);
        return;
      }
      summary.measures.forEach((measure, measureIndex) =>
        errors.push(...this.validateMeasure(measure, `Summary ${summary.key} measure ${measureIndex + 1}`)));
    });
    (definition.tiles || []).forEach((tile, index) => {
      if (!tile?.tileKey || !tile?.tileDisplay || !tile?.additionalConfig?.summaryKey
        || !tile?.additionalConfig?.value) {
        errors.push(`Tile ${index + 1} requires key, display, summaryKey, and value.`);
        return;
      }
      if (!definition.summaries.some(summary => summary.key === tile.additionalConfig.summaryKey)) {
        errors.push(`Tile ${tile.tileKey} references an unknown summary: ${tile.additionalConfig.summaryKey}.`);
      }
      const value = tile.additionalConfig.value;
      if (!value.field && (!value.numeratorField || !value.denominatorField)) {
        errors.push(`Tile ${tile.tileKey} requires a value field or numerator and denominator fields.`);
      }
      const drillDownKey = tile.additionalConfig.drillDownKey;
      if (drillDownKey && !(definition.drillDowns || []).some(drillDown => drillDown.key === drillDownKey)) {
        errors.push(`Tile ${tile.tileKey} references an unknown drill-down: ${drillDownKey}.`);
      }
      errors.push(...this.validateDrillDownMapping(
        tile.additionalConfig.drillDown,
        `Tile ${tile.tileKey}`,
        definition
      ));
    });
    (definition.widgets || []).forEach(widget => {
      errors.push(...this.validateWidget(widget));
      if (definition.layoutColumnCount && widget.columnSpan && widget.columnSpan > definition.layoutColumnCount) {
        errors.push(`Widget ${widget.key} columnSpan cannot exceed layoutColumnCount.`);
      }
      if (widget.drillDownKey
        && !(definition.drillDowns || []).some(drillDown => drillDown.key === widget.drillDownKey)) {
        errors.push(`Widget ${widget.key} references an unknown drill-down: ${widget.drillDownKey}.`);
      }
      errors.push(...this.validateDrillDownMapping(widget.drillDown, `Widget ${widget.key}`, definition));
    });
    this.addDuplicateErrors(definition.widgets.map(widget => widget.key), 'widget key', errors);
    this.addDuplicateErrors((definition.tiles || []).map(tile => tile.tileKey), 'tile key', errors);
    this.addDuplicateErrors((definition.drillDowns || []).map(drillDown => drillDown.key), 'drill-down key', errors);
    this.addDuplicateErrors(
      (definition.globalFilters || []).map(filter => filter.dataSourceItemName),
      'global-filter data source',
      errors
    );
    if (definition.defaultDrillDownKey
      && !(definition.drillDowns || []).some(drillDown => drillDown.key === definition.defaultDrillDownKey)) {
      errors.push('defaultDrillDownKey must reference a configured drill-down.');
    }
    errors.push(...this.validateDrillDownMapping(definition.defaultDrillDown, 'Default drill-down', definition));
    (definition.drillDowns || []).forEach(drillDown => {
      if (!drillDown.key || !drillDown.title || !drillDown.item || !drillDown.gridConfigKey) {
        errors.push('Each drill-down requires key, title, item, and gridConfigKey.');
      }
      const isMappedTarget = definition.defaultDrillDown?.drillDownKey === drillDown.key
        || (definition.widgets || []).some(widget => widget.drillDown?.drillDownKey === drillDown.key)
        || (definition.tiles || []).some(tile => tile.additionalConfig?.drillDown?.drillDownKey === drillDown.key);
      if (isMappedTarget && !drillDown.targetKeyField) {
        errors.push(`Drill-down ${drillDown.key} requires targetKeyField for key-based drill-downs.`);
      }
    });
    return errors;
  }

  private validateDrillDownMapping(mapping: any, label: string, definition: ReportDashboardDefinition): string[] {
    if (!mapping) {
      return [];
    }

    const errors: string[] = [];
    if (!mapping.drillDownKey || !mapping.sourceKeyField) {
      errors.push(`${label} drill-down requires drillDownKey and sourceKeyField.`);
    } else if (!(definition.drillDowns || []).some(drillDown => drillDown.key === mapping.drillDownKey)) {
      errors.push(`${label} references an unknown drill-down: ${mapping.drillDownKey}.`);
    }
    return errors;
  }

  private resolveReusableWidgets(definition: ReportDashboardDefinition): Observable<ReportDashboardDefinition> {
    const storedWidgets: any[] = definition.widgets || [];
    const reusableKeys = this.uniqueStrings(storedWidgets
      .filter(widget => !!widget?.widgetKey)
      .map(widget => widget.widgetKey));

    if (!reusableKeys.length) {
      return of(definition);
    }

    const query = this.createQuery(CONFIG_ITEMS.widget);
    const filters = new DwQueryHelper().filters;
    query.AddFilterAnd(filters.InList(`${CONFIG_ITEMS.widget}.WidgetKey`, reusableKeys));
    query.AddFilterAnd(filters.Equal(`${CONFIG_ITEMS.widget}.IsActive`, true));

    return this.ormDataService.executeQuery(query).pipe(map(rows => {
      const records = rows as unknown as ReportDashboardWidgetConfigRecord[];
      const reusableByKey = records.reduce((result, record) => {
        result[record.WidgetKey] = this.parseReusableWidgetRecord(record);
        return result;
      }, {} as { [key: string]: ReportWidgetDefinition });

      const missing = reusableKeys.filter(key => !reusableByKey[key]);
      if (missing.length) {
        throw new Error(`Reusable dashboard widget configuration was not found: ${missing.join(', ')}`);
      }

      return {
        ...definition,
        widgets: storedWidgets.map(widget => {
          if (!widget.widgetKey) {
            return widget;
          }
          const merged = this.deepMerge(reusableByKey[widget.widgetKey], widget.configOverrides || {});
          return {
            ...merged,
            key: widget.key || merged.key || widget.widgetKey
          };
        })
      };
    }));
  }

  private normalizeDashboard(definition: ReportDashboardDefinition): ReportDashboardDefinition {
    const dashboard: any = { ...definition };
    delete dashboard.contextNames;
    const normalized: ReportDashboardDefinition = {
      ...dashboard,
      filters: definition.filters || [],
      globalFilters: definition.globalFilters || [],
      summaries: (definition.summaries || []).map(summary => ({
        ...summary,
        dataSourceItemName: summary.dataSourceItemName || definition.dataSourceItemName,
        staticFilter: this.toFilterGroup(summary.staticFilter)
      })),
      tiles: (definition.tiles || []).map(tile => {
        const additionalConfig = tile.additionalConfig || ({} as any);
        return {
          ...tile,
          additionalConfig: {
            ...additionalConfig,
            detailFilter: this.toFilterGroup(additionalConfig.detailFilter),
            supportingText: additionalConfig.supportingText
              || this.buildSupportingText(additionalConfig.supportingTextTemplate)
          }
        };
      }),
      widgets: (definition.widgets || []).map(widget => this.normalizeWidget(widget)),
      drillDowns: definition.drillDowns || []
    };
    return normalized;
  }

  private normalizeWidget(widget: ReportWidgetDefinition): ReportWidgetDefinition {
    return {
      ...widget,
      request: widget.request ? Object.assign(new ChartDataRequest(), widget.request) : null,
      measures: widget.measures || [],
      computedMeasures: widget.computedMeasures || [],
      staticFilter: this.toFilterGroup(widget.staticFilter)
    };
  }

  private validateWidget(widget: ReportWidgetDefinition): string[] {
    const errors: string[] = [];
    const label = widget?.key || '(missing key)';
    if (!widget?.key || !widget?.title || !widget?.type) {
      errors.push(`Widget ${label} requires key, title, and type.`);
      return errors;
    }
    if (widget.type === 'chart' && (!widget.request?.chartType || !widget.request?.categoryField)) {
      errors.push(`Chart widget ${label} requires chartType and categoryField.`);
    }
    if (widget.type === 'chart' && !widget.measures?.length) {
      errors.push(`Chart widget ${label} requires at least one aggregate measure.`);
    }
    (widget.measures || []).forEach((measure, index) =>
      errors.push(...this.validateMeasure(measure, `Widget ${label} measure ${index + 1}`)));
    (widget.computedMeasures || []).forEach((measure, index) => {
      if (!measure?.alias || !measure?.numeratorField || !measure?.denominatorField) {
        errors.push(`Widget ${label} computed measure ${index + 1} requires alias, numeratorField, and denominatorField.`);
      }
    });
    if (widget.type === 'custom' && !widget.componentName) {
      errors.push(`Custom widget ${label} requires componentName.`);
    }
    return errors;
  }

  private validateMeasure(measure: any, label: string): string[] {
    const errors: string[] = [];
    if (!measure?.alias || !measure?.aggregateFunction) {
      errors.push(`${label} requires alias and aggregateFunction.`);
      return errors;
    }
    const fieldOptionalAggregates = ['CountAll', 'CountAllDistinct'];
    if (!measure.field && fieldOptionalAggregates.indexOf(measure.aggregateFunction) < 0) {
      errors.push(`${label} requires a field.`);
    }
    return errors;
  }

  private loadSingleRecord<T>(itemName: string, keyField: string, keyValue: string): Observable<T> {
    if (!keyValue) {
      return throwError(`A ${keyField} value is required.`);
    }
    const query = this.createQuery(itemName);
    const filters = new DwQueryHelper().filters;
    query.AddFilterAnd(filters.Equal(`${itemName}.${keyField}`, keyValue));
    query.AddFilterAnd(filters.Equal(`${itemName}.IsActive`, true));
    return this.ormDataService.executeQuery(query).pipe(mergeMap(rows => {
      if (!rows?.length) {
        return throwError(`Active ${itemName} configuration was not found: ${keyValue}`);
      }
      return of(rows[0] as unknown as T);
    }));
  }

  private loadRecords<T>(itemName: string): Observable<T[]> {
    const query = this.createQuery(itemName);
    return this.ormDataService.executeQuery(query).pipe(map(rows => rows as unknown as T[]));
  }

  private createQuery(itemName: string): DwQuery {
    const query = new DwQuery(itemName);
    query.SetFieldSettings(false, true);
    return query;
  }

  private parseJson<T>(json: any, label: string): T {
    if (json === null || json === undefined || json === '') {
      throw new Error(`Configuration JSON is missing for ${label}.`);
    }
    if (typeof json === 'object') {
      return json as T;
    }
    if (typeof json !== 'string') {
      throw new Error(`Configuration JSON has an unsupported value for ${label}.`);
    }
    try {
      return JSON.parse(json) as T;
    } catch (error) {
      throw new Error(`Configuration JSON is invalid for ${label}: ${error.message || error}`);
    }
  }

  private toFilterGroup(value: any): DwQueryFilterGroup {
    if (!value) {
      return null;
    }
    return Object.assign(new DwQueryFilterGroup(), value);
  }

  private removeFilterVisibility(definition: any): any {
    if (!definition || typeof definition !== 'object' || !Array.isArray(definition.filters)) {
      return definition;
    }
    return {
      ...definition,
      filters: definition.filters.map(filter => {
        const normalized = { ...(filter || {}) };
        delete normalized.visibleExpression;
        return normalized;
      })
    };
  }

  private buildSupportingText(template?: string) {
    if (!template) {
      return null;
    }
    return (row: any, formattedValue: string) => template.replace(/\{\{([^}]+)\}\}/g, (_match, token) => {
      const field = String(token || '').trim();
      if (field.toLowerCase() === 'formattedvalue') {
        return formattedValue;
      }
      return this.formatNumber(row?.[field]);
    });
  }

  private formatNumber(value: any): string {
    return (Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  private uniqueStrings(values: string[]): string[] {
    return (values || []).filter((value, index, source) => !!value && source.indexOf(value) === index);
  }

  private addDuplicateErrors(values: string[], label: string, errors: string[]): void {
    const duplicates = (values || []).filter((value, index, source) => !!value && source.indexOf(value) !== index);
    this.uniqueStrings(duplicates).forEach(value => errors.push(`Duplicate ${label}: ${value}.`));
  }

  private deepMerge(base: any, overrides: any): any {
    if (Array.isArray(overrides)) {
      return overrides.map(item => this.deepMerge(undefined, item));
    }
    if (overrides && typeof overrides === 'object') {
      const result = base && typeof base === 'object' && !Array.isArray(base) ? { ...base } : {};
      Object.keys(overrides).forEach(key => result[key] = this.deepMerge(result[key], overrides[key]));
      return result;
    }
    return overrides === undefined ? base : overrides;
  }

  private cloneJsonValue<T>(value: T): T {
    return value === undefined || value === null
      ? value
      : JSON.parse(JSON.stringify(value));
  }
}
